import { RegionSetStatusType } from "@app/projects/enumerators";
import { SERVICE_AREA_STATUS_DISPLAY_ORDER } from "@app/projects/network-assessment/serviceAreaStatusConstants";
import { CheckboxStateEnum } from "@app/shared/qes-standards/forms/Checkbox";
import { MultiMapExportCounty, MultiMapExportRouteState, MultiMapExportStateGroup } from "@app/projects/network-assessment/multi-map-export/interfaces";

/** Taken from the run configuration's own counties, so every pill matches at least one county. */
export const availableCountyStatuses = (states: readonly MultiMapExportStateGroup[]): RegionSetStatusType[] => {
  const present = new Set(states.flatMap((state) => state.counties.map((county) => county.status)));

  return SERVICE_AREA_STATUS_DISPLAY_ORDER.filter((status) => present.has(status));
};

/** The pill control reports selections as plain strings in click order. */
export const orderCountyStatuses = (statuses: readonly string[]): RegionSetStatusType[] =>
  SERVICE_AREA_STATUS_DISPLAY_ORDER.filter((status) => statuses.includes(status));

export const statesInScope = (
  states: readonly MultiMapExportStateGroup[],
  selectedStatuses: readonly RegionSetStatusType[],
): MultiMapExportStateGroup[] =>
  states
    .map((state) => ({ ...state, counties: state.counties.filter((county) => selectedStatuses.includes(county.status)) }))
    .filter((state) => state.counties.length > 0);

export const countiesInScope = (
  states: readonly MultiMapExportStateGroup[],
  selectedStatuses: readonly RegionSetStatusType[],
): MultiMapExportCounty[] => statesInScope(states, selectedStatuses).flatMap((state) => state.counties);

/**
 * Counties that leave scope are dropped, counties newly in scope arrive selected, and counties the
 * user unchecked while in scope stay unchecked.
 */
export const reconcileCountySelection = (
  states: readonly MultiMapExportStateGroup[],
  selectedCountyRegionSetIds: readonly number[],
  previousStatuses: readonly RegionSetStatusType[],
  nextStatuses: readonly RegionSetStatusType[],
): number[] => {
  const previouslyInScope = new Set(countiesInScope(states, previousStatuses).map((county) => county.regionSetId));
  const selected = new Set(selectedCountyRegionSetIds);

  return countiesInScope(states, nextStatuses)
    .filter((county) => selected.has(county.regionSetId) || !previouslyInScope.has(county.regionSetId))
    .map((county) => county.regionSetId);
};

const pluralize = (count: number, singular: string, plural: string): string => (count === 1 ? singular : plural);

/** "3 states · 44 counties" — what is in view, not what is selected. */
export const formatServiceAreaSummary = (states: readonly MultiMapExportStateGroup[]): string => {
  const countyCount = states.reduce((total, state) => total + state.counties.length, 0);

  return `${states.length} ${pluralize(states.length, "state", "states")} · ${countyCount} ${pluralize(countyCount, "county", "counties")}`;
};

export const formatCountyCount = (count: number): string => `${count} ${pluralize(count, "county", "counties")}`;

export const stateCheckboxState = (
  counties: readonly MultiMapExportCounty[],
  selectedCountyRegionSetIds: readonly number[],
): CheckboxStateEnum => {
  const selected = new Set(selectedCountyRegionSetIds);
  const selectedCount = counties.filter((county) => selected.has(county.regionSetId)).length;

  if (selectedCount === 0) return CheckboxStateEnum.Unchecked;
  if (selectedCount === counties.length) return CheckboxStateEnum.Checked;

  return CheckboxStateEnum.Indeterminate;
};

export interface CountySelection {
  selectedCountyRegionSetIds: number[];
  selectedCountySsas: string[];
}

/**
 * Pairs the region set ids the UI selects on with the county SSAs the export filters on, read from the same
 * `states` snapshot so they cannot disagree. The two are not interchangeable — this page fetches the
 * project's own region sets, the export job walks canonical ones — so every dispatch stores both.
 */
export const countySelectionFor = (
  states: readonly MultiMapExportStateGroup[],
  regionSetIds: readonly number[],
): CountySelection => {
  const selected = new Set(regionSetIds);

  // A county with no SSA leaves both lists. Dropping it from the SSAs alone could empty that list, and the
  // server reads an empty SSA list as "every county".
  const counties = states
    .flatMap((state) => state.counties)
    .filter((county) => selected.has(county.regionSetId) && !!county.countySsa);

  return {
    selectedCountyRegionSetIds: counties.map((county) => county.regionSetId),
    selectedCountySsas: counties.map((county) => county.countySsa),
  };
};

export interface ServiceAreaSelection extends CountySelection {
  selectedCountyStatuses: RegionSetStatusType[];
}

/** Comparable by region set id only because both sides fetch the project's own region sets, not canonical ones. */
const findLaunchCounty = (
  states: readonly MultiMapExportStateGroup[],
  selectedServiceArea?: string,
): MultiMapExportCounty | undefined => {
  if (!selectedServiceArea) return undefined;

  const regionSetId = Number(selectedServiceArea);

  return states.flatMap((state) => state.counties).find((county) => county.regionSetId === regionSetId);
};

/**
 * The county the user came from on the Network Coverage Map becomes the selection. An absent or
 * unrecognised launch value means "arrived without this", never "nothing selected", so it falls
 * back to Select All.
 */
export const initialServiceAreaSelection = (
  states: readonly MultiMapExportStateGroup[],
  launchContext?: MultiMapExportRouteState,
): ServiceAreaSelection => {
  const available = availableCountyStatuses(states);
  const launchStatuses = available.filter((status) => launchContext?.selectedCountyStatuses?.includes(status));
  const launchCounty = findLaunchCounty(states, launchContext?.selectedServiceArea);
  const baseStatuses = launchStatuses.length > 0 ? launchStatuses : available;

  // A guard, not an expected path: the map only offers counties matching its own status filter.
  const selectedCountyStatuses = launchCounty && !baseStatuses.includes(launchCounty.status)
    ? SERVICE_AREA_STATUS_DISPLAY_ORDER.filter((status) => baseStatuses.includes(status) || status === launchCounty.status)
    : baseStatuses;

  const regionSetIds = launchCounty
    ? [launchCounty.regionSetId]
    : countiesInScope(states, selectedCountyStatuses).map((county) => county.regionSetId);

  return {
    ...countySelectionFor(states, regionSetIds),
    selectedCountyStatuses,
  };
};
